// 게스트 이벤트 뷰 — 토큰 기반 세션리스 페이지.
//
// 교류전이면 호스트(team_order=1)·게스트(team_order=2) 명단/대진을 양쪽
// 클럽이 같은 화면에서 본다. draft 상태일 때는 게스트 측에서 본인 팀 명단을
// 직접 입력해 저장(POST guest-roster) — 저장되면 부모가 전달한 rerender로
// 화면을 새로 그린다.

import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { ApiError, safeJson } from "../api";
import { matchPlayerName, formatTeamName } from "../matches";
import { buildEventRankingSection, buildMatchCard } from "../matches/cards";
import { DEFAULT_CONFIG, state } from "../state";
import { $ } from "../ui/dom";
import {
  eventKindLabel,
  formatDateKo,
  formatGuestExpiresAt,
  formatTimeShort,
  statusLabel,
  surfaceLabel,
} from "../ui/format";
import { DomNode, mountPreact } from "../ui/preact-mount";

export interface GuestEventOpts {
  rerender: () => void | Promise<void>;
}

interface RosterEntry {
  display_name: string;
  gender_snapshot: "male" | "female";
}

function GuestRosterSection({
  initialRoster,
  eventId,
  token,
  rerender,
}: {
  initialRoster: RosterEntry[];
  eventId: string;
  token: string;
  rerender: () => void | Promise<void>;
}) {
  const [roster, setRoster] = useState<RosterEntry[]>(
    initialRoster.length === 0 ? [{ display_name: "", gender_snapshot: "male" }] : initialRoster,
  );
  const [saving, setSaving] = useState(false);

  function update(i: number, patch: Partial<RosterEntry>) {
    setRoster((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function remove(i: number) {
    setRoster((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length === 0 ? [{ display_name: "", gender_snapshot: "male" }] : next;
    });
  }

  function add() {
    setRoster((prev) => [...prev, { display_name: "", gender_snapshot: "male" }]);
  }

  async function save() {
    const valid = roster.filter((r) => r.display_name.trim());
    if (valid.length === 0) { alert("최소 1명의 이름을 입력해주세요."); return; }
    if (saving) return;
    setSaving(true);
    try {
      const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
      const url = `${base}/api/v1/public/events/${encodeURIComponent(eventId)}/guest-roster?token=${encodeURIComponent(token)}`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          players: valid.map((r) => ({
            display_name: r.display_name.trim(),
            gender_snapshot: r.gender_snapshot,
          })),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error?.message || "저장 실패");
      }
      alert(`${valid.length}명 명단이 저장되었어요!`);
      await rerender();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Fragment>
      <div class="stack" style="gap:8px">
        {roster.map((r, i) => (
          <div key={i} style="display:flex;gap:8px;align-items:center">
            <input
              class="input input--compact"
              type="text"
              placeholder="이름"
              value={r.display_name}
              onInput={(e) => update(i, { display_name: (e.currentTarget as HTMLInputElement).value })}
            />
            <button
              type="button"
              class="btn btn--ghost btn--sm guest-toggle"
              onClick={() => update(i, { gender_snapshot: r.gender_snapshot === "male" ? "female" : "male" })}
            >
              {r.gender_snapshot === "male" ? "남" : "여"}
            </button>
            <button
              type="button"
              class="btn btn--ghost btn--sm"
              style="color:var(--danger);min-width:0;padding:4px"
              onClick={() => remove(i)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button type="button" class="btn btn--ghost btn--sm" style="margin-top:4px" onClick={add}>
        + 선수 추가
      </button>
      <button
        type="button"
        class="btn btn--primary btn--block"
        style="margin-top:12px"
        disabled={saving}
        onClick={save}
      >
        {saving ? "저장 중…" : "명단 저장"}
      </button>
    </Fragment>
  );
}

interface GuestEventViewProps {
  detail: any;
  eventId: string;
  token: string;
  rerender: () => void | Promise<void>;
}

function ParticipantChips({ list, nameOf }: { list: any[]; nameOf: (p: any) => string }) {
  return (
    <div class="chip-group">
      {list.map((p, i) => (
        <span key={i} class="chip"><span class="chip__name">{nameOf(p)}</span></span>
      ))}
    </div>
  );
}

function GuestEventView({ detail, eventId, token, rerender }: GuestEventViewProps) {
  const ev = detail.event || {};
  const participants = detail.participants || [];
  const matches = detail.matches || [];
  const teams = detail.teams || [];
  const rounds = detail.rounds || [];
  const guestCourts = detail.courts || [];
  const roundMap: Record<number, any> = Object.fromEntries(rounds.map((r: any) => [r.round_number, r]));
  const guestSurfaceMap = new Map<string, string>();
  for (const c of guestCourts) {
    if (!c.surface_type) continue;
    const lbl = surfaceLabel(c.surface_type);
    if (lbl) guestSurfaceMap.set(c.event_court_id, lbl);
  }

  const guestClub = detail.club || {};
  const guestClubName = guestClub.short_name || guestClub.name || "";
  const guestLogoUrl = guestClub.logo_url || null;
  const isFriendlyDraft = ev.is_friendly_match && ev.status === "draft";

  const byRound = new Map<number, any[]>();
  for (const m of matches) {
    const rn = m.round_number || 0;
    if (!byRound.has(rn)) byRound.set(rn, []);
    byRound.get(rn)!.push(m);
  }

  const rankingSec = (ev.event_type === "special" && !ev.is_friendly_match && matches.length > 0)
    ? buildEventRankingSection(ev, matches, participants, teams)
    : null;

  return (
    <Fragment>
      <div class="guest-banner">
        {guestLogoUrl && <img src={guestLogoUrl} class="guest-banner__logo" />}
        <strong>{guestClubName || "게스트 뷰"}</strong>
        <span> · 대진/결과 공유</span>
      </div>

      <div class="detail-hero">
        <span class="detail-hero__chip">{statusLabel(ev.status)}</span>
        <h1 class="detail-hero__title">{ev.title || "일정"}</h1>
        <p class="detail-hero__meta">
          <span>{formatDateKo(ev.event_date)}</span>
          {ev.location && <span>· {ev.location}</span>}
          <span>· {eventKindLabel(ev)}</span>
        </p>
      </div>

      {isFriendlyDraft ? (
        <Fragment>
          <div class="section section--panel">
            <div class="section__title"><h3>우리 팀 명단 입력</h3></div>
            <p class="muted small">이름과 성별을 입력해주세요. 저장하면 상대 클럽에 전달돼요.</p>
            <GuestRosterSection
              initialRoster={participants
                .filter((p: any) => p.participant_type === "guest")
                .map((p: any) => ({
                  display_name: p.display_name || "",
                  gender_snapshot: (p.gender_snapshot as "male" | "female") || "male",
                }))}
              eventId={eventId}
              token={token}
              rerender={rerender}
            />
          </div>
          {(() => {
            const hostMembers = participants.filter((p: any) => p.participant_type !== "guest");
            if (hostMembers.length === 0) return null;
            return (
              <div class="section">
                <div class="section__title">
                  <h3>{guestClubName || "상대 클럽"} 참가자</h3>
                  <span class="count">{hostMembers.length}명</span>
                </div>
                <ParticipantChips list={hostMembers} nameOf={(p) => matchPlayerName(p)} />
              </div>
            );
          })()}
        </Fragment>
      ) : ev.is_friendly_match ? (
        (() => {
          const ours = participants.filter((p: any) => p.participant_type === "guest");
          const theirs = participants.filter((p: any) => p.participant_type !== "guest");
          const ourLabel = ev.opponent_club_name || "우리 팀";
          const theirLabel = guestClubName || "상대 클럽";
          const groups = [
            { label: ourLabel, list: ours },
            { label: theirLabel, list: theirs },
          ].filter((g) => g.list.length > 0);
          return (
            <div class="section">
              <div class="section__title">
                <h3>참가자</h3>
                <span class="count">{participants.length}명</span>
              </div>
              {groups.map((g) => (
                <Fragment key={g.label}>
                  <div class="gender-label">{g.label} {g.list.length}명</div>
                  <ParticipantChips list={g.list} nameOf={(p) => p.display_name || "-"} />
                </Fragment>
              ))}
            </div>
          );
        })()
      ) : (
        <div class="section">
          <div class="section__title">
            <h3>참가자</h3>
            <span class="count">{participants.length}명</span>
          </div>
          {participants.length === 0 ? (
            <div class="card empty">참가자가 없어요</div>
          ) : (
            (() => {
              const groups = [
                { label: "남", list: participants.filter((p: any) => p.gender_snapshot === "male") },
                { label: "여", list: participants.filter((p: any) => p.gender_snapshot === "female") },
                { label: "기타", list: participants.filter((p: any) => p.gender_snapshot !== "male" && p.gender_snapshot !== "female") },
              ].filter((g) => g.list.length > 0);
              return groups.map((g) => (
                <Fragment key={g.label}>
                  <div class="gender-label">{g.label} {g.list.length}명</div>
                  <ParticipantChips list={g.list} nameOf={(p) => matchPlayerName(p)} />
                </Fragment>
              ));
            })()
          )}
        </div>
      )}

      {teams.length > 0 && (
        <div class="section">
          <div class="section__title">
            <h3>팀 편성</h3>
            <span class="count">{teams.length}팀</span>
          </div>
          {(ev.is_friendly_match
            ? [...teams].sort((a: any, b: any) => (b.team_order || 0) - (a.team_order || 0))
            : teams
          ).map((team: any) => (
            <div key={team.team_id} class="card">
              <div style="font-weight:700;margin-bottom:4px">{formatTeamName(team.team_name)}</div>
              <div class="field__hint">
                {(team.members || []).map((m: any) => m.display_name || "-").join(", ") || "-"}
              </div>
            </div>
          ))}
        </div>
      )}

      {rankingSec && <DomNode node={rankingSec} />}

      <div class="section">
        <div class="section__title">
          <h3>경기</h3>
          <span class="count">{matches.length}개</span>
        </div>
        {matches.length === 0 ? (
          <div class="card empty">아직 대진이 없어요</div>
        ) : (
          [...byRound.entries()].map(([rn, roundMatches]) => {
            const rd = roundMap[rn];
            const timeStr = rd
              ? `${formatTimeShort(rd.scheduled_start_at)} ~ ${formatTimeShort(rd.scheduled_end_at)}`
              : "";
            return (
              <Fragment key={rn}>
                <div class="round-label">
                  <span>라운드 {rn}</span>
                  {timeStr && <span class="round-label__time">{timeStr}</span>}
                </div>
                {roundMatches.map((m: any) => (
                  <DomNode
                    key={m.match_id}
                    node={buildMatchCard(m, ev, { readOnly: true, surfaceMap: guestSurfaceMap })}
                  />
                ))}
              </Fragment>
            );
          })
        )}
      </div>

      {detail.expires_at && (
        <p class="guest-footer">이 링크는 {formatGuestExpiresAt(detail.expires_at)}까지 유효해요.</p>
      )}

      <div class="guest-powered">
        <p style="margin:0">페어매치로 운영되는 클럽이에요</p>
        <p class="guest-powered__contact">
          문의: <a href="mailto:fairmatch.team@gmail.com" style="color:inherit">fairmatch.team@gmail.com</a>
        </p>
      </div>
    </Fragment>
  );
}

export async function renderGuestEvent(
  view: HTMLElement,
  eventId: string,
  token: string,
  opts: GuestEventOpts,
): Promise<void> {
  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let detail: any;
  try {
    const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
    const url = `${base}/api/v1/public/events/${encodeURIComponent(eventId)}/guest?token=${encodeURIComponent(token)}`;
    const resp = await fetch(url);
    const text = await resp.text();
    const payload = text ? safeJson(text) : null;
    if (!resp.ok) {
      const err = (payload && (payload.error || payload)) || {};
      throw new ApiError(err.message || `요청 실패 (${resp.status})`, err.code || `HTTP_${resp.status}`, resp.status);
    }
    detail = (payload && typeof payload === "object" && "data" in payload) ? payload.data : payload;
  } catch (e: any) {
    view.innerHTML = "";
    const msg = e.code === "GUEST_LINK_EXPIRED" ? "게스트 링크가 만료되었어요. 주최자에게 새 링크를 요청해 주세요."
      : e.code === "GUEST_TOKEN_INVALID" ? "링크가 올바르지 않아요. 주최자에게 새 링크를 요청해 주세요."
      : e.code === "GUEST_TOKEN_REQUIRED" ? "링크에 토큰이 없어요."
      : e.code === "GUEST_LINK_NOT_FOUND" ? "이 일정은 게스트 링크가 발급되지 않았어요."
      : (e.message || "불러오기 실패");
    view.append(mountPreact(
      <div class="error-card">
        <h2>게스트 접근 불가</h2>
        <p>{msg}</p>
      </div>,
    ));
    return;
  }

  const titleEl = $("#topbar-title");
  if (titleEl) titleEl.textContent = (detail.event && detail.event.title) || "게스트 보기";

  view.innerHTML = "";
  view.append(mountPreact(
    <GuestEventView detail={detail} eventId={eventId} token={token} rerender={opts.rerender} />,
  ));
}
